import React, { Fragment, type ReactElement, type ReactNode } from "react";
import { CalendarDays } from "lucide-react";
import { cn } from "@ndd/ui";
import {
  RAIL_TODAY_LINES,
  railTodayToggleLabel,
  type RailAppointmentsMark,
  type RailTodayRow,
  type RailTodayView,
} from "../../lib/rail-appointments";
import { RailRowChevron, railRowClass } from "./always-rail-parts";
import {
  SiderailIcon,
  SiderailLabelClip,
  SiderailSectionLabel,
  SiderailTodayRowContent,
  SiderailTooltip,
  siderailTodayRowClass,
} from "./siderail-parts";

// The single rail's Appointments entry, drawn (NO-754): a row that links to
// the Appointments list, its Today chevron, and Today nested under it like an
// RD's chats. A leaf module (`import React`, relative imports, no router, no
// queries) so `tsx --test` renders it; the composer (rail-appointments.tsx)
// decides what shows (lib/rail-appointments.ts) and supplies real links.

export interface RailAppointmentsLinkProps {
  /** An open Appointment, or null for the Appointments list. */
  appointmentId: string | null;
  current: boolean;
  className: string;
  ariaLabel?: string;
  children: ReactNode;
}

export type RenderAppointmentsLink = (props: RailAppointmentsLinkProps) => ReactElement;

export interface RailTodayDisclosure {
  open: boolean;
  onToggle: () => void;
}

export function RailAppointmentsRow({
  label,
  tooltip,
  ariaLabel,
  mark,
  collapsed,
  disclosure,
  renderLink,
}: {
  label: ReactNode;
  tooltip: string;
  ariaLabel?: string;
  mark: RailAppointmentsMark;
  collapsed: boolean;
  /** The Today chevron; without it the row is a plain link. */
  disclosure?: RailTodayDisclosure;
  renderLink: RenderAppointmentsLink;
}): ReactElement {
  const link = (
    <SiderailTooltip label={tooltip} collapsed={collapsed}>
      {renderLink({
        appointmentId: null,
        current: mark === "current",
        className: railRowClass(mark),
        ariaLabel,
        children: (
          <>
            <SiderailIcon icon={CalendarDays} />
            <SiderailLabelClip collapsed={collapsed}>
              <span className={cn("block truncate", disclosure ? "pr-9" : "pr-2")}>
                {label}
              </span>
            </SiderailLabelClip>
          </>
        ),
      })}
    </SiderailTooltip>
  );
  if (!disclosure) return link;

  return (
    <div className="relative">
      {link}
      <RailRowChevron
        label={railTodayToggleLabel(disclosure.open)}
        open={disclosure.open}
        onToggle={disclosure.onToggle}
        collapsed={collapsed}
      />
    </div>
  );
}

const QUIET_LINE = "px-2.5 py-1.5 text-body-sm text-subtle";

function RailTodayRows({
  rows,
  renderLink,
}: {
  rows: RailTodayRow[];
  renderLink: RenderAppointmentsLink;
}): ReactElement {
  if (rows.length === 0) return <p className={QUIET_LINE}>{RAIL_TODAY_LINES.empty}</p>;

  return (
    <>
      {rows.map((row) => (
        <Fragment key={row.id}>
          {renderLink({
            appointmentId: row.id,
            current: row.current,
            className: siderailTodayRowClass(row.upNext, row.current),
            children: (
              <SiderailTodayRowContent
                time={row.time}
                name={row.name}
                upNext={row.upNext}
              />
            ),
          })}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Today, indented under the Appointments row so it lines up with the row's
 * label: the heading with what is left, then each remaining Appointment, or
 * one quiet line.
 */
export function RailToday({
  view,
  renderLink,
}: {
  view: RailTodayView;
  renderLink: RenderAppointmentsLink;
}): ReactElement | null {
  if (view.kind === "none") return null;

  return (
    <div
      role="group"
      aria-label="Today's Appointments"
      className="flex flex-col pb-1 pl-10"
    >
      {view.kind === "line" ? (
        <p className={QUIET_LINE}>{view.line}</p>
      ) : (
        <>
          <SiderailSectionLabel label={view.heading} count={view.count} />
          <RailTodayRows rows={view.rows} renderLink={renderLink} />
        </>
      )}
    </div>
  );
}
